import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { capitalizeWords, consultantTypeCssClass, generalCurrencyFormatter } from '../../../lib/helper/formatting';
import CcsdCodeCostAndTimelineIcon from '../../ccsdCodeCost/CcsdCodeCostAndTimelineIcon';
import FormControl from 'react-bootstrap/FormControl';
import MASpinner from '../../common/MASpinner';
import { sortHealthInsurersByName, CCSD_CODE_TYPE_PROCEDURE_CODE } from '../../../lib/helper/general';
import Alert from 'react-bootstrap/Alert';

class InvoiceBatchCcsdCodeCostTable extends React.Component {
  constructor(props) {
    super(props);
    this._searchedDefaultRefs = new Set();
  }

  setCurrentHealthInsurerId(event) {
    const dropdownValue = event.target.value;
    const vals = dropdownValue.split('-');
    if (this.props.onHealthInsurerChange) {
      this.props.onHealthInsurerChange(vals && vals[0] ? vals[0] : false, dropdownValue);
    }
    this.props.setSelectedConsultantHealthInsurer(vals && vals[1] ? vals[1] : false);
  }

  getCcsdCodeSpan(ccsdCodeCost, isDefault = false) {
    return (
      <span key={ccsdCodeCost.id} className={`${isDefault ? 'font-italic' : ''}`}>
        <CcsdCodeCostAndTimelineIcon
          ccsdCodeCostToUse={ccsdCodeCost}
          consultantType={this.props.consultantType}
          isDefaultCost={isDefault}
          isOverride={!!ccsdCodeCost.isOverride}
          pricingRuleVariant="badge"
          emptyLabel="n/a"
        />
      </span>
    );
  }

  getRuleDerivedCostSpan(derivedCost) {
    const { consultantType } = this.props;
    const price = derivedCost.resolvedPrices?.[consultantType?.code];
    const breakdown = derivedCost.resolvedPriceBreakdowns?.[consultantType?.code];
    if (price == null) {
      return <span className="text-muted">n/a</span>;
    }
    return (
      <span>
        {generalCurrencyFormatter(price)}
        {breakdown && (
          <span
            title={breakdown}
            className="badge badge-info ml-1"
            style={{ fontSize: '0.65em', verticalAlign: 'middle', cursor: 'default' }}>
            Rule applied
          </span>
        )}
      </span>
    );
  }

  getCellContent(consultantHealthInsurer, ccsdCode) {
    const costs = this.props.ccsdCodeCosts.filter((ccsdCodeCost) => {
      return (
        String(ccsdCodeCost.ccsdCode.reference) === String(ccsdCode.reference) &&
        Number(ccsdCodeCost.consultantHealthInsurer.id) === Number(consultantHealthInsurer.id)
      );
    });

    if (costs.length === 0) {
      if (consultantHealthInsurer.feeAssured && ccsdCode.ccsdCodeType && ccsdCode.ccsdCodeType.code === CCSD_CODE_TYPE_PROCEDURE_CODE) {
        const defaultCost = (this.props.defaultCcsdCodeCosts || []).find(
          (dc) =>
            dc.ccsdCode &&
            String(dc.ccsdCode.reference) === String(ccsdCode.reference) &&
            Number(dc.healthInsurer.id) === Number(consultantHealthInsurer.healthInsurer.id),
        );
        if (defaultCost) {
          return <div>{this.getCcsdCodeSpan(defaultCost, true)}</div>;
        }
      }
      if (consultantHealthInsurer.feeAssured) {
        return <span className="text-muted">n/a</span>;
      }
      // While this consultant health insurer's own bespoke costs are still loading (e.g. a
      // non-analogue insurer only fetched on demand via the insurer dropdown), `costs.length === 0`
      // does not yet mean "no bespoke cost exists" - it may simply mean the data hasn't arrived.
      // Show a spinner rather than risk masking a real (and possibly higher) bespoke cost with the
      // rule-derived suggestion below.
      if (this.props.isFetchingCcsdCodeCosts) {
        return <MASpinner />;
      }
      // Analogue (non-override) insurers are always loaded up front; any other insurer's bespoke
      // costs are only fetched once selected from the dropdown. Until we can confirm this insurer's
      // costs have actually been fetched, `costs.length === 0` is not trustworthy either - treat it
      // the same as "still loading" rather than falling back to the rule-derived suggestion, which
      // would otherwise mask a real (and possibly higher) bespoke cost that simply hasn't loaded yet.
      const isAnalogueInsurer = consultantHealthInsurer.healthInsurer.isAnalogue && !consultantHealthInsurer.healthInsurer.canHaveOverrideLabel;
      const hasLoadedBespokeCosts = isAnalogueInsurer || (this.props.loadedConsultantHealthInsurers || []).includes(consultantHealthInsurer['@id']);
      if (!hasLoadedBespokeCosts) {
        return <MASpinner />;
      }
      // Rule-derived costs are per-consultant and carry no insurer dimension, so they must only be
      // offered for columns the API would actually apply a pricing rule to. These are the same two
      // conditions as PricingResolverService::rulesApplyTo(): the insurer must be eligible for
      // pricing rules (every non-analogue insurer plus Self-Pay - MED-946), and the code must be a
      // procedure. Without this the suggestion leaks into columns like "Did Not Attend".
      const insurerCanHavePricingRules = consultantHealthInsurer.healthInsurer.canHavePricingRules === true;
      const isProcedureCode = ccsdCode.ccsdCodeType && ccsdCode.ccsdCodeType.code === CCSD_CODE_TYPE_PROCEDURE_CODE;

      if (insurerCanHavePricingRules && isProcedureCode) {
        const derivedCost = (this.props.ruleDerivedCosts || []).find(
          (dc) => String(dc.ccsdCode?.reference) === String(ccsdCode.reference),
        );
        if (derivedCost) {
          return <div>{this.getRuleDerivedCostSpan(derivedCost)}</div>;
        }
      }
      return <span className="text-muted">n/a</span>;
    }

    return costs.map((ccsdCodeCost) => (
      <div key={ccsdCodeCost.id} data-key={ccsdCodeCost.id}>
        {this.getCcsdCodeSpan(ccsdCodeCost)}
        {ccsdCodeCost.defaultCcsdCodeCostNote && (
          <Alert variant={'info'} className={'mt-2'}>
            <small style={{ whiteSpace: 'pre-wrap' }}>{ccsdCodeCost.defaultCcsdCodeCostNote}</small>
          </Alert>
        )}
      </div>
    ));
  }

  getCcsdCodeCell(consultantHealthInsurer, ccsdCode) {
    return (
      <td className={'text-center'} key={consultantHealthInsurer.healthInsurer.id}>
        {this.getCellContent(consultantHealthInsurer, ccsdCode)}
      </td>
    );
  }

  getCcsdCodeRow(ccsdCode, analogues, selectedOthers) {
    return (
      <tr key={ccsdCode.id} className={ccsdCode.isDefault ? 'table-row--default-ccsd-code-cost' : ''}>
        <th title={ccsdCode.name && ccsdCode.name.length > 0 ? ccsdCode.name : ''}>
          {ccsdCode.name}
          <br />
          <span className="font-weight-normal">{ccsdCode.reference}</span> <small>({capitalizeWords(String(ccsdCode.ccsdCodeType.code))} Code)</small>
        </th>

        {analogues.map((consultantHealthInsurer) => this.getCcsdCodeCell(consultantHealthInsurer, ccsdCode))}
        {selectedOthers.map((consultantHealthInsurer) => this.getCcsdCodeCell(consultantHealthInsurer, ccsdCode))}
      </tr>
    );
  }

  buildAllCodes() {
    const { ccsdCodes = [], defaultCcsdCodeCosts = [] } = this.props;
    const seenRefs = new Set(ccsdCodes.map((c) => String(c.reference)));
    const defaultOnlyCodes = [];
    for (const dc of defaultCcsdCodeCosts) {
      if (dc.ccsdCode && !seenRefs.has(String(dc.ccsdCode.reference))) {
        dc.ccsdCode.isDefault = true;
        seenRefs.add(String(dc.ccsdCode.reference));
        defaultOnlyCodes.push(dc.ccsdCode);
      }
    }
    return [...ccsdCodes, ...defaultOnlyCodes];
  }

  setCodeFilter(e) {
    const value = e.target.value;
    if (this.props.onCodeFilterChange) {
      this.props.onCodeFilterChange(value);
    }
    if (value.length >= 2 && this.props.onCodeFilterSearch) {
      const allCodes = this.buildAllCodes();
      const hasMatch = allCodes.some((c) => c.reference.toString().toLocaleLowerCase().includes(value.toLocaleLowerCase()));
      if (!hasMatch && !this._searchedDefaultRefs.has(value)) {
        this._searchedDefaultRefs.add(value);
        this.props.onCodeFilterSearch(value);
      }
    }
  }

  render() {
    const { consultant, consultantType } = this.props;
    if (!consultant.consultantHealthInsurers) {
      return null;
    }
    const isMultiType = consultant.consultantTypes.length > 1;

    const allCodes = this.buildAllCodes();
    const consultantHealthInsurerAnalogues = consultant.consultantHealthInsurers.filter(
      (chi) => chi.healthInsurer.isAnalogue && !chi.healthInsurer.canHaveOverrideLabel,
    );
    const consultantHealthInsurersAndOthers = consultant.consultantHealthInsurers
      .filter((chi) => !chi.healthInsurer.isAnalogue || chi.healthInsurer.canHaveOverrideLabel)
      .sort(sortHealthInsurersByName);
    const selectedOthers = consultantHealthInsurersAndOthers.filter((chi) => Number(chi.healthInsurer.id) === Number(this.props.currentHealthInsurerId));
    return (
      <Row>
        <Col>
          <div className="mb-3">
            <div className="d-block d-sm-none">
              <p>Sorry - the fee table can only be viewed on a wider device</p>
            </div>

            <Table hover bordered className="mediaccounts-batch-invoice-ccsd-table mb-0 d-none d-sm-table bg-light">
              <thead>
                {isMultiType && (
                  <tr>
                    <th colSpan={999} className={`text-center py-2 consultant-type-header--${consultantTypeCssClass(consultantType.code)}`}>
                      <strong>{consultantType.displayName} Fees</strong>
                    </th>
                  </tr>
                )}
                <tr>
                  <th className="stuck-row-1">
                    Code
                    <div className="ml-4 d-none d-sm-inline-flex">
                      <FormControl size={'sm'} placeholder="Filter by code..." aria-label="Type to filter by code" value={this.props.codeFilter} onChange={this.setCodeFilter.bind(this)} />
                    </div>
                  </th>
                  {consultantHealthInsurerAnalogues.map((chi) => (
                    <th className="batch-invoice-ccsd-table-title-cell stuck-row-1" key={chi.healthInsurer.id}>
                      {chi.healthInsurer.name}
                    </th>
                  ))}
                  <th className={'text-right stuck-row-1'}>
                    <div className={'form-group mb-0'}>
                      <select className={'form-control'} value={this.props.selectedInsurerDropdownValue || ''} onChange={this.setCurrentHealthInsurerId.bind(this)}>
                        <option value=""> - Select Insurer -</option>
                        {consultantHealthInsurersAndOthers.map((chi) => (
                          <option
                            className={chi.healthInsurer.canHaveOverrideLabel ? 'font-italic' : ''}
                            key={`i-` + chi.healthInsurer.id}
                            value={chi.healthInsurer.id + '-' + chi.id}>
                            {chi.healthInsurer.canHaveOverrideLabel && chi.overrideLabel ? chi.overrideLabel : chi.healthInsurer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="stuck-row-2">Fee Assured?</th>

                  {consultantHealthInsurerAnalogues.map((chi) => (
                    <th className={'text-center stuck-row-2'} key={`s-` + chi.healthInsurer.id}>
                      <Form.Check type="switch" defaultChecked={chi.feeAssured} id={`self-pay-${chi.healthInsurer.id}`} disabled />
                    </th>
                  ))}
                  {selectedOthers.map((chi) => (
                      <th className={'text-center stuck-row-2'} key={`s2-` + chi.healthInsurer.id}>
                        <Form.Check type="switch" defaultChecked={chi.feeAssured} id={`self-pay-${chi.healthInsurer.id}`} disabled />
                      </th>
                    ))}
                </tr>
                <tr>
                  <th className="stuck-row-3">Provider No</th>
                  {consultantHealthInsurerAnalogues.map((chi) => (
                    <th className={'text-center stuck-row-3'} key={`r` + chi.healthInsurer.id}>
                      {chi.providerReference}
                    </th>
                  ))}

                  {selectedOthers.map((chi) => (
                      <th className={'text-center stuck-row-3'} key={`r` + chi.healthInsurer.id}>
                        {chi.providerReference}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody>
                {allCodes
                  .filter((ccsdCode) => this.props.codeFilter.length < 2 || ccsdCode.reference.toString().toLocaleLowerCase().includes(this.props.codeFilter.toLocaleLowerCase()))
                  .map((ccsdCode) => this.getCcsdCodeRow(ccsdCode, consultantHealthInsurerAnalogues, selectedOthers))}
              </tbody>
            </Table>
          </div>
        </Col>
      </Row>
    );
  }
}

export default InvoiceBatchCcsdCodeCostTable;
